// Student credit records in and out of a spreadsheet.
//
// Most of this work arrives in bulk: a form teacher marks a term's lateness in one sitting, a
// discipline master enters a whole week's merits. Typing them one modal at a time is the reason
// those records end up not being entered at all.
//
// TWO THINGS THIS WILL NOT DO.
//
// It will not invent a student. Records are matched to the roll by student number or address, and a
// row that matches nobody is reported rather than created -- a mistyped number must not become a
// record about a child who does not exist, sitting in a balance nobody can trace.
//
// It will not invent an item. The award and punishment list is the school's own vocabulary and it
// carries the points; a row naming something not on that list is reported, not guessed at. The
// points come from the item, so a school that revalues "Late for class" does not have to reconcile
// a spreadsheet that still says the old number -- unless the file states a value outright, which is
// the same override the manual form allows.

// The BOM Excel writes at the front of a UTF-8 file. Spelled as an escape because the character
// itself is invisible in source.
const BOM = /^\uFEFF/;

/** Header matching that survives spacing, capitals, punctuation, a BOM, and Chinese headings. */
const normHeader = (h) => String(h || '').replace(BOM, '').toLowerCase().replace(/[^a-z0-9一-鿿]/g, '');

const HEADER_ALIASES = {
  sid: ['sid', 'studentid', 'studentno', 'studentnumber', 'regno', '學號', '學生編號'],
  email: ['email', 'studentemail', '電郵'],
  itemCode: ['itemcode', 'code', 'itemid', 'item', '項目', '項目代碼'],
  date: ['date', 'recorddate', 'on', '日期'],
  balance: ['balance', 'balancechange', 'points', 'point', 'score', '分數', '加減分'],
  remarks: ['remarks', 'remark', 'note', 'notes', 'comment', '備註'],
  staff: ['staff', 'staffincharge', 'teacher', 'by', 'initial', '負責人', '教師'],
};

const findColumn = (headers, aliases) => headers.find(h => aliases.includes(normHeader(h)));

/** A date the school typed, as an ISO day. Blank means "use today", which the caller supplies. */
export const cleanDate = (raw, fallback) => {
  const t = String(raw ?? '').trim();
  if (!t) return fallback;
  // Already ISO, possibly with a time on it.
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  // What Excel writes for a date-formatted cell in this part of the world: D/M/YYYY.
  const dmy = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return '';
};

/**
 * Turn parsed CSV rows into records, resolved against the roll and the school's own item list.
 *
 * Returns `{ entries, problems }` and never throws. Nothing is written until the caller has shown
 * the problems: an import that silently drops nine rows of a term's lateness is worse than one that
 * refuses, because nobody finds out until a report is wrong.
 */
// `t` is optional and defaults to the English it is given: the parser is tested under plain node,
// where there is no dictionary, and the import preview shows these reasons verbatim.
export const parseCreditCsv = (rows, { students = [], items = [], schoolYear = '', today = '', t = (_k, _v, fb) => fb } = {}) => {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const col = Object.fromEntries(
    Object.entries(HEADER_ALIASES).map(([key, aliases]) => [key, findColumn(headers, aliases)]),
  );

  const bySid = new Map(students.filter(s => s.sid).map(s => [String(s.sid).trim(), s]));
  const byEmail = new Map(students.filter(s => s.email).map(s => [String(s.email).toLowerCase(), s]));
  const byCode = new Map(items.filter(i => i.code).map(i => [normHeader(i.code), i]));
  // A school exports with the item's own wording in it, so accept that back as well.
  const byText = new Map(items.filter(i => i.description).map(i => [normHeader(i.description), i]));

  const entries = [];
  const problems = [];

  rows.forEach((row, i) => {
    const line = i + 2; // +1 for the header row, +1 because people count from one
    const rawSid = col.sid ? String(row[col.sid] ?? '').trim() : '';
    const rawEmail = col.email ? String(row[col.email] ?? '').trim().toLowerCase() : '';
    if (!rawSid && !rawEmail) {
      problems.push({ line, reason: t('studentCredit.importProblems.noStudentId', {}, 'No student number or address on this row') });
      return;
    }
    const student = (rawEmail && byEmail.get(rawEmail)) || (rawSid && bySid.get(rawSid));
    if (!student) {
      problems.push({ line, who: rawSid || rawEmail, reason: t('studentCredit.importProblems.notOnRoll', {}, 'Nobody on the roll has that student number or address') });
      return;
    }

    const rawItem = col.itemCode ? String(row[col.itemCode] ?? '').trim() : '';
    if (!rawItem) {
      problems.push({ line, who: student.name, reason: t('studentCredit.importProblems.noItem', {}, 'No award or punishment named') });
      return;
    }
    const item = byCode.get(normHeader(rawItem)) || byText.get(normHeader(rawItem));
    if (!item) {
      problems.push({ line, who: student.name, reason: t('studentCredit.importProblems.unknownItem', { item: rawItem }, `"${rawItem}" is not on this school's award and punishment list`) });
      return;
    }

    const date = cleanDate(col.date ? row[col.date] : '', today);
    if (!date) {
      problems.push({ line, who: student.name, reason: t('studentCredit.importProblems.badDate', { date: row[col.date] }, `Could not read the date "${row[col.date]}"`) });
      return;
    }

    // The item carries the points. A value in the file overrides it, exactly as the manual form
    // lets somebody type a different number for a one-off.
    const rawBalance = col.balance ? String(row[col.balance] ?? '').trim() : '';
    let balanceChange = Number(item.balance) || 0;
    if (rawBalance !== '') {
      const n = Number(rawBalance.replace(/^\+/, ''));
      if (Number.isFinite(n)) balanceChange = n;
      else problems.push({ line, who: student.name, reason: t('studentCredit.importProblems.badPoints', { points: rawBalance }, `Could not read the points "${rawBalance}" -- used the item's own value`), warning: true });
    }

    entries.push({
      sid: student.sid,
      studentEmail: student.email || '',
      guardianEmails: student.guardianEmails || [],
      schoolYear,
      itemCode: item.code,
      nature: item.nature || '',
      description: item.description || '',
      balanceChange,
      date,
      remarks: col.remarks ? String(row[col.remarks] ?? '').trim() : '',
      staffInCharge: col.staff ? String(row[col.staff] ?? '').trim() : '',
      // Display only, for the preview table.
      _name: student.name,
      _class: student.currentClass,
      _classNumber: student.classNumber,
    });
  });

  return { entries, problems, columns: col };
};

/** What each record looks like as a spreadsheet row. Re-importable: the keys are in it. */
export const recordToRow = (r, student) => ({
  Date: r.date || '',
  Class: student?.currentClass || '',
  No: student?.classNumber || '',
  SID: r.sid || '',
  Name: student?.name || '',
  'Chinese Name': student?.chineseName || '',
  'Item Code': r.itemCode || '',
  Item: r.description || '',
  Nature: r.nature || '',
  Balance: r.balanceChange ?? 0,
  Remarks: r.remarks || '',
  Staff: r.staffInCharge || '',
});

export const CSV_COLUMNS = [
  'Date', 'Class', 'No', 'SID', 'Name', 'Chinese Name',
  'Item Code', 'Item', 'Nature', 'Balance', 'Remarks', 'Staff',
];

/**
 * Rebuild both balance maps from every record there is.
 *
 * The module keeps running totals that each screen increments as it writes. A bulk import that
 * incremented once per row would be a thousand transactions to do arithmetic we can do in one pass
 * -- and every missed increment in an accumulator is permanent, because nothing ever recomputes it
 * unless somebody asks. So an import recomputes instead of incrementing: the records are the truth
 * and this is what they add up to.
 */
export const balancesFromRecords = (records) => {
  const yearly = {};
  const lifetime = {};
  (records || []).forEach((r) => {
    if (!r?.sid) return;
    const delta = Number(r.balanceChange) || 0;
    const yr = r.schoolYear || 'unknown';
    yearly[yr] = yearly[yr] || {};
    yearly[yr][r.sid] = (yearly[yr][r.sid] || 0) + delta;
    lifetime[r.sid] = (lifetime[r.sid] || 0) + delta;
  });
  return { yearly, lifetime };
};
